import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { observer } from "mobx-react";
import { UploadFeedback, Button } from '@ann/web-components';
import { Header, Tabs } from "./components";
import { Drives, MyWorkspace, SharedWithMe } from "./pages";
import { DrivePageMainWrapper, DrivesPageContainer, UploadFeedbackWrapper, BackToTopButton } from "./DrivesPage.styled";
import { DrivesTabsEnum } from "./enums/drivesTabs";
import { useDrivesStores } from "./hook/use-drives.hook";
import { useStore } from "../../../hooks";
import { useStoreNotifications } from "./hook/use-store-notifications.hook";
import { useScrollToTop } from "../../../contexts/ScrollToTopContext";
import { useFileUpload } from "./hook/use-file-upload.hook";

const UploadFeedbackSection = observer(({ onClose }) => {
  const { documentsStore } = useStore();
  const { retryUpload } = useFileUpload();
  
  const handleFileRemove = useCallback((id) => {
    const doc = documentsStore.temporaryDocuments.find(d => d.uploadId === id);
    if (doc) {
      documentsStore.removeTemporaryDocument(id);
    }
  }, [documentsStore]);

  const handleRetry = useCallback((id) => {
    const doc = documentsStore.temporaryDocuments.find(d => d.uploadId === id);
    if (doc) {
      retryUpload(doc);
    }
  }, [documentsStore, retryUpload]);

  const uploadFiles = documentsStore.temporaryDocuments.map((doc) => {
    const progress = documentsStore.uploadProgress[doc.name];
    let status = 'ready';
    if (doc.state === 'loading') {
      status = 'uploading';
    } else if (doc.state === 'error') {
      status = 'uploadFailed';
    } else if (doc.state === 'active') {
      status = 'ready';
    }
    return {
      id: doc.uploadId,
      name: doc.name,
      progress: progress?.percentage || 0,
      status: status,
      onRetry: doc.state === 'error' ? () => handleRetry(doc.uploadId) : undefined
    };
  });

  if (uploadFiles.length === 0) return null;

  return (
    <UploadFeedbackWrapper>
      <UploadFeedback
        files={uploadFiles}
        onClose={onClose}
        onFileRemove={handleFileRemove}
        onRetry={handleRetry}
      />
    </UploadFeedbackWrapper>
  );
});

UploadFeedbackSection.displayName = 'UploadFeedbackSection';

const DrivesPageContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [selectedTab, setSelectedTab] = useState(() => {
    if (location.pathname.includes("/my-workspace")) {
      return DrivesTabsEnum.MY_WORKSPACE;
    } else if (location.pathname.includes("/shared-with-me")) {
      return DrivesTabsEnum.SHARED_WITH_ME;
    } else {
      return DrivesTabsEnum.DRIVES;
    }
  });
  
  const { myWorkspacesStore, drivesViewStore } = useDrivesStores();
  const { documentsStore } = useStore();

  // Local scroll management for BackToTop button
  const [showBackToTop, setShowBackToTop] = useState(false);
  const scrollContainerRef = useRef(null);

  // useRef doesn't cause re-renders and maintains value between renders
  const drivesInitialized = useRef(false);

  // Load drives once when parent component mounts
  useEffect(() => {
    const loadDrives = async () => {
      if (!drivesInitialized.current && drivesViewStore?.loadDrivesData) {
        const hasDrives = drivesViewStore?.drivesStore?.drives?.length > 0;
        if (!hasDrives) {
          await drivesViewStore.loadDrivesData();
        }
        drivesInitialized.current = true;
      }
    };
    loadDrives();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Executes only once on mount

  // Setup scroll listener for BackToTop button
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const SCROLL_THRESHOLD = 300; // Show button after scrolling 300px

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      setShowBackToTop(scrollTop > SCROLL_THRESHOLD);
    };

    // Add scroll listener
    container.addEventListener('scroll', handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => container.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top function
  const scrollToTop = useCallback(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  }, []);

  const notifications = useMemo(() => [
    {
      condition: documentsStore.uploadSuccess,
      type: 'success',
      description: "Your documents have been uploaded",
      onClose: () => documentsStore.setUploadSuccess(false)
    },
    {
      condition: documentsStore.uploadFailed,
      type: 'error',
      title: "Some documents could not be uploaded",
      description: documentsStore.errorMessage || "Check your internet connection or try again.",
      onClose: () => documentsStore.setUploadFailed(false)
    },
    {
      condition: documentsStore.isErrorCreateDocumentEntry,
      type: 'error',
      title: "Document creation failed",
      description: documentsStore.errorMessage || "Failed to create document entry in database",
      onClose: () => documentsStore.setIsErrorCreateDocumentEntry(false)
    },
    {
      condition: documentsStore.isErrorCreateFileEntry,
      type: 'error',
      title: "File entry creation failed",
      description: documentsStore.errorMessage || "Failed to create file entry for upload",
      onClose: () => documentsStore.setIsErrorCreateFileEntry(false)
    },
    {
      condition: documentsStore.isErrorUploadToS3,
      type: 'error',
      title: "Upload to storage failed",
      description: documentsStore.errorMessage || "Failed to upload file to storage",
      onClose: () => documentsStore.setIsErrorUploadToS3(false)
    },
    {
      condition: documentsStore.isErrorAttachDocument,
      type: 'error',
      title: "Document attachment failed",
      description: documentsStore.errorMessage || "Failed to attach document after upload",
      onClose: () => documentsStore.setIsErrorAttachDocument(false)
    }
  ], [documentsStore]);

  // Map tabs to URL routes
  const getTabRoute = (tabIndex) => {
    switch (tabIndex) {
      case DrivesTabsEnum.DRIVES:
        return "/v2/drives";
      case DrivesTabsEnum.MY_WORKSPACE:
        return "/v2/drives/my-workspace";
      case DrivesTabsEnum.SHARED_WITH_ME:
        return "/v2/drives/shared-with-me";
      default:
        return "/v2/drives";
    }
  };

  const getTabFromRoute = (pathname) => {
    if (pathname.includes("/my-workspace")) {
      return DrivesTabsEnum.MY_WORKSPACE;
    } else if (pathname.includes("/shared-with-me")) {
      return DrivesTabsEnum.SHARED_WITH_ME;
    } else {
      return DrivesTabsEnum.DRIVES;
    }
  };

  useEffect(() => {
    const tabFromUrl = getTabFromRoute(location.pathname);
    setSelectedTab(tabFromUrl);
  }, [location.pathname]);

  // Toast notifications for upload operations
  useStoreNotifications(notifications);

  const handleTabChange = (tabIndex) => {
    setSelectedTab(tabIndex);
    const newRoute = getTabRoute(tabIndex);
    navigate(newRoute, { replace: true });
  };

  const renderTabContent = () => {
    switch (selectedTab) {
      case DrivesTabsEnum.DRIVES:
        return <Drives />;
      case DrivesTabsEnum.MY_WORKSPACE:
        return <MyWorkspace />;
      case DrivesTabsEnum.SHARED_WITH_ME:
        return <SharedWithMe />;
      default:
        return <Drives />;
    }
  };

  const shouldShowTabs = !(selectedTab === DrivesTabsEnum.MY_WORKSPACE && myWorkspacesStore?.currentPage === 'upload');

  return (
    <>
      <DrivePageMainWrapper ref={scrollContainerRef}>
        <DrivesPageContainer>
        <Header
          selectedTab={selectedTab}
          onTabChange={handleTabChange}
        />
        {shouldShowTabs && <Tabs selectedTab={selectedTab} onTabChange={handleTabChange} />}
        {renderTabContent()}
        </DrivesPageContainer>
      </DrivePageMainWrapper>

      <UploadFeedbackSection 
        onClose={() => {
          documentsStore.temporaryDocuments.forEach(doc => {
            documentsStore.removeTemporaryDocument(doc.uploadId || doc.name);
          });
        }}
      />

      <BackToTopButton $visible={showBackToTop}>
        <Button
          type="primary"
          size="medium"
          label="Back to top"
          icon="chevronUp"
          iconPosition="left"
          onClick={scrollToTop}
          pill={true}
        />
      </BackToTopButton>
    </>
  );
};

DrivesPageContent.displayName = 'DrivesPageContent';

// ✅ Provider moved to index.jsx to share store across all routes
export default DrivesPageContent;
